'use client';

import { useState } from 'react';

interface AtlasRow {
  code: string;
  name: string;
  flag: string;
  publicHolidays: number;
  paidLeave: number;
  totalDaysOff: number;
  leaveSource: string;
}

type SortKey = 'name' | 'publicHolidays' | 'paidLeave' | 'totalDaysOff';
type SortDir = 'asc' | 'desc';

interface VacationAtlasTableProps {
  rows: AtlasRow[];
  lang: string;
  countrySlugs?: Record<string, string>;
}

export function VacationAtlasTable({ rows, countrySlugs = {} }: VacationAtlasTableProps) {
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('totalDaysOff');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const lowerFilter = filter.toLowerCase();
  const filtered = lowerFilter
    ? rows.filter(r => r.name.toLowerCase().includes(lowerFilter))
    : rows;

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === 'name') {
      return sortDir === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    const diff = a[sortKey] - b[sortKey];
    return sortDir === 'asc' ? diff : -diff;
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-text-primary">
        All Countries Compared
      </h2>

      <input
        type="search"
        placeholder="Search countries\u2026"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        className="input input-bordered mb-4 w-full max-w-md"
        aria-label="Filter countries"
      />

      <div className="overflow-x-auto">
        <table className="table-zebra table w-full">
          <thead>
            <tr>
              <th
                className="cursor-pointer text-left"
                onClick={() => toggleSort('name')}
              >
                Country{sortIndicator('name')}
              </th>
              <th
                className="cursor-pointer text-right"
                onClick={() => toggleSort('publicHolidays')}
              >
                Public Holidays{sortIndicator('publicHolidays')}
              </th>
              <th
                className="cursor-pointer text-right"
                onClick={() => toggleSort('paidLeave')}
              >
                Paid Leave{sortIndicator('paidLeave')}
              </th>
              <th
                className="cursor-pointer text-right"
                onClick={() => toggleSort('totalDaysOff')}
              >
                Total Days Off{sortIndicator('totalDaysOff')}
              </th>
              <th className="text-left">Source</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => (
              <tr key={row.code}>
                <td>
                  <span className="mr-1.5">{row.flag}</span>
                  {countrySlugs[row.code] ? (
                    <a href={`/${countrySlugs[row.code]}`} className="text-accent-primary hover:underline">
                      {row.name}
                    </a>
                  ) : (
                    row.name
                  )}
                </td>
                <td className="text-right">{row.publicHolidays}</td>
                <td className="text-right">{row.paidLeave}</td>
                <td className="text-right font-semibold">{row.totalDaysOff}</td>
                <td className="text-xs text-text-secondary">{row.leaveSource}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-text-secondary">
        Showing {sorted.length} of {rows.length} countries.
        Paid leave = statutory minimum annual leave mandated by labour law.
        Public holidays from national calendar data.
      </p>
    </div>
  );
}
